import { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { faPlane } from '@fortawesome/free-solid-svg-icons';

import { GetAirportMapGroupDto, GetMissionsFromMapDto, MissionType } from '../api/api.g';
import { MapMarker } from '../models/MapModels';
import { MissionsHelper } from '../models/helpers/missionsHelper';
import { LoggedState } from '../models/AuthModelsCommon';
import { FilterItemProps } from '../components/common/filter/FilterItem';
import { isMissionAvailableForDiscovery } from '../models/helpers/missionAvailabilityHelper';

export interface UseMarkersParams {
  missions: GetMissionsFromMapDto[] | undefined;
  airportMapGroups: GetAirportMapGroupDto[];
  filterItems: FilterItemProps[];
  selectedFilterItems: FilterItemProps[] | undefined;
  selectedMissionFilterType?: number;
  authLoggedState: LoggedState;
  t: (key: string) => string;
}

export interface UseMarkersResult {
  markers: MapMarker[];
  updateMarkers: (filters: string[], missionFilterTypeOverride?: number) => void;
  clearMarkers: () => void;
}

const FILTER_TYPE_ALL = 0;
const FILTER_TYPE_CLASSIC = 1;
const FILTER_TYPE_FLASH = 2;

const normalizeTagValue = (value?: string) => {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const splitMissionTagValues = (value?: string) => {
  const normalized = normalizeTagValue(value);
  if (!normalized) {
    return [];
  }

  const tokenized = normalized
    .split(/[;,/|]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

  return Array.from(new Set([normalized, ...tokenized]));
};

const resolveMissionType = (mission: { missionType?: MissionType }) => {
  return mission.missionType ?? MissionType.Regular;
};

const resolveIsFlashMission = (mission: { isFlashMission?: boolean }) => {
  return !!mission.isFlashMission;
};

const mapFontAwesomeIconToSvg = (icon?: any, fillColor: string = '#000000') => {
  const iconData = icon?.icon;
  if (!iconData || iconData.length < 5) {
    return undefined;
  }

  const width = iconData[0];
  const height = iconData[1];
  const pathData = iconData[4];
  const paths = Array.isArray(pathData) ? pathData : [pathData];
  const svgPaths = paths
    .filter((path) => !!path)
    .map((path) => `<path d="${path}" />`)
    .join('');

  if (!svgPaths) {
    return undefined;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" fill="${fillColor}">${svgPaths}</svg>`;
};

const useMarkers = ({
  missions,
  airportMapGroups,
  filterItems,
  selectedFilterItems,
  selectedMissionFilterType,
  authLoggedState,
  t,
}: UseMarkersParams): UseMarkersResult => {
  const [markers, setMarkers] = useState<MapMarker[]>([]);

  const airportPlaneSvg = useMemo(() => mapFontAwesomeIconToSvg(faPlane, '#000000'), []);

  const updateMarkers = (filters: string[], missionFilterTypeOverride?: number) => {
    const regularMissions = missions ?? [];
    const selectedMissionFilterTypeValue =
      missionFilterTypeOverride ?? selectedMissionFilterType ?? FILTER_TYPE_ALL;
    const showClassicOnly = selectedMissionFilterTypeValue === FILTER_TYPE_CLASSIC;
    const showFlashOnly = selectedMissionFilterTypeValue === FILTER_TYPE_FLASH;
    let filterValues: string[] = [];

    if (filters && filters.length > 0) {
      filterValues = filterItems.filter((f) => filters.indexOf(`${f.id}`) > -1).map((f) => f.name);
    } else {
      filterValues = filterItems.map((f) => f.name);
    }

    const selectedTagValues = new Set(filterValues.map((x) => normalizeTagValue(x)).filter((x) => x.length > 0));

    const filteredMissions = regularMissions.filter((x) => {
      const latitude = Number(x.geocoordinate?.y);
      const longitude = Number(x.geocoordinate?.x);
      const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
      const missionType = resolveMissionType(x);
      const isFlashMission = resolveIsFlashMission(x);
      const includeByMissionType =
        missionType === MissionType.Regular &&
        (!showClassicOnly || !isFlashMission) &&
        (!showFlashOnly || isFlashMission);
      let withTag: boolean = true;

      if (filters && filters.length > 0) {
        const missionTagTokens = splitMissionTagValues(x.tags);
        withTag = missionTagTokens.some((token) => selectedTagValues.has(token));

        if (!withTag) {
          const missionTagText = normalizeTagValue(x.tags);
          withTag = Array.from(selectedTagValues).some((selectedTag) => {
            return missionTagText.includes(selectedTag) || selectedTag.includes(missionTagText);
          });
        }
      }

      return hasCoordinates && includeByMissionType && withTag;
    });

    const nonAirportMarkers: MapMarker[] = filteredMissions.map((mission) => {
      const { icon, faIcon } = MissionsHelper.getMissionIcon(mission.tags);
      const latitude = Number(mission.geocoordinate?.y);
      const longitude = Number(mission.geocoordinate?.x);
      const missionType = resolveMissionType(mission);
      const stableId = mission.branchId ?? `${mission.name}-${latitude}-${longitude}`;
      const defaultPin = authLoggedState === LoggedState.Anonymous || isMissionAvailableForDiscovery(mission) ? 'default' : 'grey';
      const seed = [
        stableId,
        mission.name ?? '',
        mission.tags ?? '',
        missionType,
        resolveIsFlashMission(mission) ? 'flash' : 'classic',
        defaultPin,
        latitude.toFixed(6),
        longitude.toFixed(6),
      ].join(':');

      return {
        title: mission.name,
        id: stableId,
        selected: false,
        isFlashMission: resolveIsFlashMission(mission) && missionType === MissionType.Regular,
        missionType: missionType,
        coordinate: { latitude, longitude },
        svgIcon: mission.svgIcon ?? icon,
        faIcon: faIcon,
        seed,
        defaultPin,
      };
    });

    const showAirportMarkers = selectedMissionFilterTypeValue === FILTER_TYPE_ALL;
    const filteredAirportGroups = showAirportMarkers
      ? filters && filters.length > 0
        ? airportMapGroups.filter((group) => {
            const groupTags = group.tags ?? [];
            if (groupTags.length <= 0) {
              return true;
            }

            return groupTags.some((tag) => !!tag.id && filters.includes(`${tag.id}`));
          })
        : airportMapGroups
      : [];

    const airportMarkers: MapMarker[] = filteredAirportGroups
      .map((group) => {
        if (!group.representativeBranchId || !group.geocoordinate?.x || !group.geocoordinate?.y) {
          return undefined;
        }

        const latitude = Number(group.geocoordinate.y);
        const longitude = Number(group.geocoordinate.x);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          return undefined;
        }

        const groupMissions = [...(group.highOrderMissions ?? []), ...(group.missions ?? []), ...(group.lowOrderMissions ?? [])];
        const hasAcceptableMission = groupMissions.some((mission) => isMissionAvailableForDiscovery(mission));
        const defaultPin = authLoggedState === LoggedState.Anonymous || hasAcceptableMission ? 'default' : 'grey';

        return {
          title: group.airportName ?? t('Airport'),
          id: group.representativeBranchId,
          selected: false,
          isFlashMission: false,
          missionType: MissionType.Airport,
          coordinate: { latitude, longitude },
          svgIcon: airportPlaneSvg,
          faIcon: faPlane,
          seed: `airport-${group.airportId ?? group.representativeBranchId}-${defaultPin}-${latitude.toFixed(6)}-${longitude.toFixed(6)}`,
          defaultPin,
        } as MapMarker;
      })
      .filter((marker): marker is MapMarker => !!marker);

    const mappedMarkers = [...nonAirportMarkers, ...airportMarkers];

    if (Platform.OS === 'android') {
      const uniqueByCoordinate = new globalThis.Map<string, MapMarker>();
      for (const marker of mappedMarkers) {
        const key = `${marker.coordinate.latitude.toFixed(6)}|${marker.coordinate.longitude.toFixed(6)}`;
        if (!uniqueByCoordinate.has(key)) {
          uniqueByCoordinate.set(key, marker);
        }
      }
      setMarkers(Array.from(uniqueByCoordinate.values()));
    } else {
      setMarkers(mappedMarkers);
    }
  };

  const clearMarkers = () => {
    setMarkers([]);
  };

  useEffect(() => {
    if (!missions && airportMapGroups.length <= 0) {
      return;
    }

    updateMarkers((selectedFilterItems ?? []).map((x) => x.id));
  }, [missions, airportMapGroups, selectedFilterItems, filterItems, selectedMissionFilterType]);

  return {
    markers,
    updateMarkers,
    clearMarkers,
  };
};

export default useMarkers;
